/**
 * useModuleName — persona-aware module display name (Forwarder TMS Wave 1.4).
 *
 * Module names in `common:modules.*` are normally LITERAL — tenant terminology
 * overrides deliberately never rename a module in the sidebar/picker/navigation
 * (see CLAUDE.md i18n rules). This hook is the single, explicit exception:
 * when the tenant's forwarder persona (`sellsFreightServices`) is on, the
 * Shipments module renders as "Freight Operations" everywhere (sidebar, page
 * headers, breadcrumbs, module picker, AI chat tooling refs).
 *
 * The override is gated on the persona flag, NOT the terminology system, so it
 * flips immediately mid-session when the admin toggles the flag (usePersona is a
 * React Query subscription — no reload). To add another persona-renamed module,
 * extend PERSONA_OVERRIDES; resolution stays in one place.
 */
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { usePersona, type Persona } from './usePersona';
import type { ModuleConfig } from '../config/modules.config';

/** Minimal shape needed to resolve a module's name — `id` selects the override, `nameKey` is the literal fallback. */
type ModuleNameInput = Pick<ModuleConfig, 'id' | 'nameKey'>;

/** Minimal shape needed to resolve a module's description — `id` selects the override. */
type ModuleDescriptionInput = Pick<ModuleConfig, 'id' | 'descriptionKey'>;

/** The i18n keys a persona override can supply for a module. */
interface PersonaModuleKeys {
  nameKey: string;
  descriptionKey: string;
}

/**
 * Module id → { persona flag → i18n keys } to use when that flag is on. Typed
 * against `Persona` so it stays in sync as new persona flags are added. Only
 * `shipments` participates today; keep keys flat under `common:modules.*`.
 *
 * The description travels with the name: a card headed "Freight Operations" that
 * still describes "reference data for load units, ports, airports" is the rename
 * leaking. Both keys are overridden together, never one without the other.
 */
const PERSONA_OVERRIDES: Record<string, Partial<Record<keyof Persona, PersonaModuleKeys>>> = {
  shipments: {
    sellsFreightServices: {
      nameKey: 'modules.shipmentsFreight',
      descriptionKey: 'modules.shipmentsFreightDescription',
    },
  },
};

/** Resolves the persona override for a module, or undefined when none applies. */
function resolveOverride(
  moduleId: string,
  sellsFreightServices: boolean,
): PersonaModuleKeys | undefined {
  if (!sellsFreightServices) return undefined;
  return PERSONA_OVERRIDES[moduleId]?.sellsFreightServices;
}

/**
 * Pure resolver: returns the i18n key to translate for a module given the
 * tenant persona. Falls back to the module's literal `nameKey` when no persona
 * override applies. Exported for unit testing and non-hook call sites.
 */
export function resolveModuleNameKey(
  module: ModuleNameInput,
  sellsFreightServices: boolean,
): string {
  return resolveOverride(module.id, sellsFreightServices)?.nameKey ?? module.nameKey;
}

/**
 * Pure resolver twin of `resolveModuleNameKey` for the module's one-line
 * description (module grid card, module picker). Falls back to the module's
 * literal `descriptionKey`.
 */
export function resolveModuleDescriptionKey(
  module: ModuleDescriptionInput,
  sellsFreightServices: boolean,
): string {
  return resolveOverride(module.id, sellsFreightServices)?.descriptionKey ?? module.descriptionKey;
}

/**
 * Returns a stable `(module) => translatedName` function. Reads the persona once
 * and translates against the `common` namespace so it works regardless of the
 * caller's default namespace. Re-renders (and re-resolves) when the persona flag
 * changes, so module names flip without a page reload.
 */
export function useModuleName(): (module: ModuleNameInput) => string {
  const { t } = useTranslation('common');
  const { data: persona } = usePersona();
  const sellsFreightServices = persona?.sellsFreightServices === true;

  return useCallback(
    (module: ModuleNameInput) => t(resolveModuleNameKey(module, sellsFreightServices)),
    [t, sellsFreightServices],
  );
}

/**
 * Returns a stable `(module) => translatedDescription` function — the
 * description twin of `useModuleName`, for the module grid card and picker.
 */
export function useModuleDescription(): (module: ModuleDescriptionInput) => string {
  const { t } = useTranslation('common');
  const { data: persona } = usePersona();
  const sellsFreightServices = persona?.sellsFreightServices === true;

  return useCallback(
    (module: ModuleDescriptionInput) => t(resolveModuleDescriptionKey(module, sellsFreightServices)),
    [t, sellsFreightServices],
  );
}
